import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataTablesList } from '@shared/definitions';

@Injectable({
  providedIn: 'root',
})
export class EmailApiService {
  constructor(private http: HttpClient) {}

  getEmails = (companyId: string, params?) =>
    this.http.get<DataTablesList<EmailListItem>>(`/api/v1.0/companies/${companyId}/emails`, { params });

  previewEmail = (companyId: string, emailId: number) =>
    this.http.get<{ html: string }>(`/api/v1.0/companies/${companyId}/emails/${emailId}/preview`);

  resendEmail = (companyId: string, emailId: number) =>
    this.http.post<{ success: boolean }>(`/api/v1.0/companies/${companyId}/emails/${emailId}/resend`, {});

  // Full detail for a single email, fetched lazily when a row is expanded. The rendered body is
  // NOT part of this payload — it stays on previewEmail().
  getEmailDetail = (companyId: string, emailId: number) =>
    this.http.get<EmailDetailItem>(`/api/v1.0/companies/${companyId}/emails/${emailId}/details`);

  // Streams a single attachment as a blob (goes through the auth interceptor, so the Bearer token
  // is sent — a plain anchor href would not carry it). The caller triggers the browser save.
  downloadEmailAttachment = (companyId: string, emailId: number, name: string) =>
    this.http.get(`/api/v1.0/companies/${companyId}/emails/${emailId}/attachments`, {
      params: { name },
      responseType: 'blob',
    });

  // Dashboard-visible email types (VisibleInDashboard = true on the backend verb type). The type
  // filter list is built from this so the backend flag is the single source of truth — no hardcoded
  // duplicate that can drift from the server.
  getEmailTypes = () => this.http.get<EmailDashboardType[]>(`/api/v1.0/email-verbs/dashboard-types`);
}

export interface EmailListItem {
  id: number;
  recipients: string;
  subject: string;
  type_id: string;
  category: string;
  status: string;
  external_error_result: string;
  sent: string;
  order_id: string;
  order_number: string;
  reservation_id: string;
}

export interface EmailDashboardType {
  id: string;
  name: string;
  category: string;
  display_name_key: string;
}

export interface EmailAddress {
  name: string;
  email: string;
}

export interface EmailAttachment {
  name: string;
  // "Attachment" or "Inline"
  type: string;
}

export interface EmailClick {
  url: string;
  date: string;
}

export interface EmailDetailItem {
  id: number;
  subject: string;
  type_id: string;
  status: string;
  campaign_id: string;
  domain: string;
  sender_name: string;
  sender_email: string;
  external_result: string;
  external_error_result: string;

  // Status timeline — each is null until the matching Mailgun webhook fires.
  sent: string;
  delivered: string;
  opened: string;
  clicked: string;
  rejected: string;
  failed_permanently: string;
  failed_temporarily: string;
  unsubscribed: string;
  delivery_time: string;

  recipients: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];

  order_id: string;
  order_number: string;
  reservation_id: string;
  user_id: string;
  shop_id: string;
  subscription_instance_id: string;

  country: string;
  region: string;
  city: string;
  client_type: string;
  client_os: string;
  device_type: string;
  client_name: string;

  dkim: boolean;
  test_mode: boolean;
  tracking_enabled: boolean;
  tracking_click_enabled: boolean;
  tracking_open_enabled: boolean;

  attachments: EmailAttachment[];
  clicks: EmailClick[];
}
